import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, euro, fmtDate, openAuthPdf, formatApiErrorDetail } from "@/lib/api";
import { useSuspendedClientGuard } from "@/context/SuspendedClientAlert";
import { InternalLayout } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PreventivoDialog } from "@/components/PreventivoDialog";
import { PreventivoEventiDialog } from "@/components/PreventivoEventiDialog";
import { OppStatoBadge } from "@/pages/Opportunita";
import { PrevStatoBadge } from "@/pages/Preventivi";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, PlusIcon as Plus, FilePdfIcon as FilePdf, PencilSimpleIcon as PencilSimple, TrashIcon as Trash, ClockCounterClockwiseIcon as ClockCounterClockwise } from "@phosphor-icons/react";

const Field = ({ label, value }) => (<div><div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div><div className="text-sm mt-0.5">{value || "—"}</div></div>);

export default function OpportunitaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [opp, setOpp] = useState(null);
  const [clients, setClients] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [eventi, setEventi] = useState(null);

  const load = useCallback(() => {
    api.get(`/crm/opportunita/${id}`).then(({ data }) => setOpp(data)).catch(() => { toast.error("Opportunità non trovata"); nav("/commerciale/opportunita"); });
  }, [id, nav]);
  useEffect(() => { load(); api.get("/clients").then(({ data }) => setClients(data)); }, [load]);
  useSuspendedClientGuard(opp?.cliente_id);

  const delPrev = async (p) => {
    if (!window.confirm(`Eliminare il preventivo ${p.numero_preventivo || ""}?`)) return;
    try { await api.delete(`/crm/preventivi/${p.id}`); toast.success("Preventivo eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!opp) return <InternalLayout><div className="p-12 text-muted-foreground">Caricamento…</div></InternalLayout>;

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12 max-w-5xl">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="opp-back"><ArrowLeft size={16} /> Indietro</button>
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="flex items-center gap-3"><h1 className="font-heading text-2xl font-bold">{opp.titolo || "Opportunità"}</h1><OppStatoBadge stato={opp.stato} /></div>
            <p className="text-muted-foreground mt-1">{opp.cliente_nome}</p>
          </div>
          <Button onClick={() => { setEditing(null); setDlg(true); }} className="rounded-sm gap-2" data-testid="opp-new-preventivo-btn"><Plus size={16} weight="bold" /> Nuovo preventivo</Button>
        </div>

        <Card className="rounded-sm border-border shadow-none p-6 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <Field label="Categoria" value={opp.categoria} />
            <Field label="Origine" value={opp.origine} />
            <Field label="Responsabile" value={opp.responsabile} />
            <Field label="Valore stimato" value={opp.valore_stimato != null ? euro(opp.valore_stimato) : null} />
            <Field label="Probabilità" value={opp.probabilita != null ? `${opp.probabilita}%` : null} />
            <Field label="Data apertura" value={fmtDate(opp.data_apertura)} />
            <Field label="Chiusura prevista" value={fmtDate(opp.data_chiusura_prevista)} />
            <Field label="Prossimo follow-up" value={fmtDate(opp.prossimo_followup)} />
          </div>
          {opp.descrizione && <div className="mt-5"><Field label="Descrizione" value={opp.descrizione} /></div>}
          {opp.note_interne && <div className="mt-4 border-t border-border pt-4"><Field label="Note interne" value={opp.note_interne} /></div>}
        </Card>

        <Card className="rounded-sm border-border shadow-none">
          <div className="p-4 border-b border-border"><h3 className="font-heading font-semibold">Preventivi collegati</h3></div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Numero</TableHead><TableHead>Oggetto</TableHead><TableHead className="text-right">Importo</TableHead>
              <TableHead>Scadenza</TableHead><TableHead>Stato</TableHead><TableHead>Visibile</TableHead><TableHead>PDF</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(opp.preventivi || []).length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground" data-testid="opp-no-prev">Nessun preventivo collegato.</TableCell></TableRow>
                : opp.preventivi.map((p) => (
                  <TableRow key={p.id} data-testid="opp-prev-row">
                    <TableCell className="font-medium">{p.numero_preventivo || "—"}{p.scaduto && <Badge variant="outline" className="ml-2 rounded-sm bg-rose-50 text-rose-700 border-rose-200">Scaduto</Badge>}</TableCell>
                    <TableCell>{p.oggetto || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{euro(p.importo)}</TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">{fmtDate(p.data_scadenza) || "—"}</TableCell>
                    <TableCell><PrevStatoBadge stato={p.stato} /></TableCell>
                    <TableCell>{p.visibile_area_cliente ? <Badge variant="outline" className="rounded-sm bg-green-50 text-green-700 border-green-200">Sì</Badge> : <span className="text-muted-foreground text-sm">No</span>}</TableCell>
                    <TableCell>{p.pdf_presente ? <button className="text-primary inline-flex items-center gap-1 text-sm" data-testid="opp-prev-pdf" onClick={() => openAuthPdf(`/crm/preventivi/${p.id}/pdf`)}><FilePdf size={16} /> Apri</button> : <span className="text-muted-foreground text-sm">—</span>}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <button className="text-muted-foreground hover:text-foreground p-1" data-testid="opp-prev-storico" onClick={() => setEventi(p)} title="Storico eventi"><ClockCounterClockwise size={16} /></button>
                      <button className="text-muted-foreground hover:text-foreground p-1" data-testid="opp-prev-edit" onClick={() => { setEditing(p); setDlg(true); }}><PencilSimple size={16} /></button>
                      <button className="text-muted-foreground hover:text-rose-600 p-1" data-testid="opp-prev-del" onClick={() => delPrev(p)}><Trash size={16} /></button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <PreventivoDialog open={dlg} onOpenChange={setDlg} clients={clients} fixedOpp={editing ? null : opp} editing={editing} opportunita={[opp]} onSaved={load} />
      <PreventivoEventiDialog open={!!eventi} onOpenChange={(o) => !o && setEventi(null)} preventivo={eventi} />
    </InternalLayout>
  );
}
